// ═══════════════════════════════════════════════════════════════
// MODAL: Comprobantes de Pago del Empleado
// 3 tipos: Nómina Regular | Bonificación | Regalía Pascual
// Panel izquierdo: lista | Panel derecho: preview iframe
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { X, FileText, Download, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import {
  getNominaReceipts,
  getBonificacionReceipts,
  getRegaliaReceipts,
  getNominaPayslipBlob,
  getBonificacionPayslipBlob,
  getRegaliaPayslipBlob,
} from '../../../api/employeeReceipts';

const fmt = (v) =>
  'RD$ ' + parseFloat(v || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TIPOS = [
  { id: 'nomina',       label: 'Nómina',       color: 'blue'   },
  { id: 'bonificacion', label: 'Bonificación',  color: 'purple' },
  { id: 'regalia',      label: 'Regalía',       color: 'red'    },
];

// ── Etiqueta descriptiva por tipo ────────────────────────────
function buildLabel(tipo, rec) {
  if (tipo === 'nomina') {
    const q = rec.quincena ? ` · ${rec.quincena}` : '';
    return `${rec.periodo}${q} · ${rec.tipo}`;
  }
  if (tipo === 'bonificacion') {
    return rec.nombre || `Bonificación ${rec.anio}`;
  }
  return `Regalía Pascual ${rec.anio}`;
}

function buildSubLabel(tipo, rec) {
  if (tipo === 'nomina') return rec.fecha_pago ? `Pago: ${rec.fecha_pago}` : '';
  return rec.fecha_pago ? `Pagado: ${rec.fecha_pago}` : '';
}

function buildMonto(tipo, rec) {
  if (tipo === 'nomina')       return rec.neto_a_pagar;
  if (tipo === 'bonificacion') return rec.neto_a_pagar ?? rec.monto_final;
  return rec.monto;
}

// ── Fetch PDF blob según tipo ─────────────────────────────────
function fetchBlob(tipo, rec, employeeId) {
  if (tipo === 'nomina')
    return getNominaPayslipBlob(rec.payroll_id, employeeId);
  if (tipo === 'bonificacion')
    return getBonificacionPayslipBlob(rec.bonificacion_id, rec.detalle_id);
  return getRegaliaPayslipBlob(rec.regalia_payroll_id, rec.record_id);
}

// ── Filename para descarga ────────────────────────────────────
function buildFilename(tipo, rec) {
  if (tipo === 'nomina')
    return `nomina_${rec.periodo}${rec.quincena ? '_' + rec.quincena : ''}.pdf`;
  if (tipo === 'bonificacion')
    return `bonificacion_${rec.anio}.pdf`;
  return `regalia_${rec.anio}.pdf`;
}

// ─────────────────────────────────────────────────────────────
export default function ComprobantesModal({ employee, onClose }) {
  const [tipo,         setTipo]         = useState('nomina');
  const [records,      setRecords]      = useState([]);
  const [loadingList,  setLoadingList]  = useState(false);
  const [selectedRec,  setSelectedRec]  = useState(null);
  const [previewUrl,   setPreviewUrl]   = useState(null);
  const [loadingPrev,  setLoadingPrev]  = useState(false);
  const [downloading,  setDownloading]  = useState(false);
  const [listError,    setListError]    = useState(null);
  const [prevError,    setPrevError]    = useState(null);
  const prevUrlRef = useRef(null);

  // Limpiar blob URL anterior al cambiar
  const setPreview = (url) => {
    if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
    prevUrlRef.current = url;
    setPreviewUrl(url);
  };

  // Cargar lista al cambiar tipo
  useEffect(() => {
    if (!employee?.id) return;
    setRecords([]);
    setSelectedRec(null);
    setPreview(null);
    setListError(null);
    setLoadingList(true);

    const loaders = {
      nomina:       () => getNominaReceipts(employee.id),
      bonificacion: () => getBonificacionReceipts(employee.id),
      regalia:      () => getRegaliaReceipts(employee.id),
    };

    loaders[tipo]()
      .then(rows => setRecords(rows))
      .catch(() => setListError('Error al cargar los comprobantes'))
      .finally(() => setLoadingList(false));
  }, [tipo, employee?.id]); // eslint-disable-line

  // Cleanup al desmontar
  useEffect(() => () => { if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current); }, []);

  const handleSelect = async (rec) => {
    if (selectedRec?.record_id === rec.record_id && selectedRec?.detalle_id === rec.detalle_id) return;
    setSelectedRec(rec);
    setPreview(null);
    setPrevError(null);
    setLoadingPrev(true);
    try {
      const blob = await fetchBlob(tipo, rec, employee.id);
      setPreview(URL.createObjectURL(blob));
    } catch {
      setPrevError('No se pudo generar la vista previa');
    } finally {
      setLoadingPrev(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedRec) return;
    setDownloading(true);
    try {
      const blob = await fetchBlob(tipo, selectedRec, employee.id);
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = buildFilename(tipo, selectedRec);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // silencioso — el preview ya habría fallado
    } finally {
      setDownloading(false);
    }
  };

  const tipoConfig = TIPOS.find(t => t.id === tipo);

  const tabClasses = (t) => {
    const active = tipo === t.id;
    const colors = { blue: 'border-blue-500 text-blue-600', purple: 'border-purple-500 text-purple-600', red: 'border-red-500 text-red-600' };
    return `flex-1 py-2 text-xs font-semibold border-b-2 transition-colors ${
      active ? colors[t.color] : 'border-transparent text-gray-400 hover:text-gray-600'
    }`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-900">Comprobantes de Pago</h2>
            <p className="text-xs text-gray-400 mt-0.5">{employee.nombre} {employee.apellido}</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body: lista + preview */}
        <div className="flex flex-1 overflow-hidden">

          {/* ── Panel izquierdo: lista ──────────────────────── */}
          <div className="w-72 flex-shrink-0 border-r border-gray-200 flex flex-col overflow-hidden">

            {/* Tabs de tipo */}
            <div className="flex border-b border-gray-200 flex-shrink-0">
              {TIPOS.map(t => (
                <button key={t.id} onClick={() => setTipo(t.id)} className={tabClasses(t)}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* Lista */}
            <div className="flex-1 overflow-y-auto">
              {loadingList && (
                <div className="flex items-center justify-center py-10 text-gray-400">
                  <RefreshCw size={16} className="animate-spin mr-2" />
                  <span className="text-xs">Cargando...</span>
                </div>
              )}

              {listError && (
                <div className="flex items-center gap-2 m-3 p-3 bg-red-50 rounded-lg text-xs text-red-600">
                  <AlertCircle size={14} /> {listError}
                </div>
              )}

              {!loadingList && !listError && records.length === 0 && (
                <div className="flex flex-col items-center justify-center py-14 px-4 text-center">
                  <FileText size={28} className="text-gray-300 mb-2" />
                  <p className="text-xs text-gray-400">Sin comprobantes de {tipoConfig.label.toLowerCase()}</p>
                </div>
              )}

              {!loadingList && records.map((rec, idx) => {
                const isSelected = selectedRec === rec;
                return (
                  <button
                    key={idx}
                    onClick={() => handleSelect(rec)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-100 transition-colors ${
                      isSelected ? 'bg-blue-50 border-l-2 border-l-blue-500' : 'hover:bg-gray-50'
                    }`}
                  >
                    <p className="text-xs font-semibold text-gray-800 truncate">{buildLabel(tipo, rec)}</p>
                    {buildSubLabel(tipo, rec) && (
                      <p className="text-xs text-gray-400 mt-0.5">{buildSubLabel(tipo, rec)}</p>
                    )}
                    <p className="text-xs font-semibold text-green-700 mt-1">{fmt(buildMonto(tipo, rec))}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Panel derecho: preview ──────────────────────── */}
          <div className="flex-1 flex flex-col overflow-hidden">

            {/* Toolbar del preview */}
            {selectedRec && (
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                <p className="text-xs font-medium text-gray-600 truncate">
                  {buildLabel(tipo, selectedRec)}
                </p>
                <button
                  onClick={handleDownload}
                  disabled={downloading || loadingPrev}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex-shrink-0 ml-3"
                >
                  {downloading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                  Descargar
                </button>
              </div>
            )}

            {/* Preview area */}
            <div className="flex-1 overflow-hidden bg-gray-100">
              {!selectedRec && (
                <div className="flex flex-col items-center justify-center h-full text-center px-8">
                  <FileText size={40} className="text-gray-300 mb-3" />
                  <p className="text-sm font-medium text-gray-400">Selecciona un comprobante</p>
                  <p className="text-xs text-gray-300 mt-1">Se mostrará una vista previa aquí</p>
                </div>
              )}

              {selectedRec && loadingPrev && (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <Loader2 size={24} className="animate-spin mr-2" />
                  <span className="text-sm">Cargando comprobante...</span>
                </div>
              )}

              {selectedRec && prevError && (
                <div className="flex flex-col items-center justify-center h-full gap-3">
                  <AlertCircle size={28} className="text-red-400" />
                  <p className="text-sm text-red-500">{prevError}</p>
                </div>
              )}

              {selectedRec && previewUrl && !loadingPrev && (
                <iframe
                  src={previewUrl}
                  title="Comprobante de pago"
                  className="w-full h-full border-0"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
